import {
  Form,
  Link,
  useActionData,
  useNavigation,
  useSearchParams,
  type ActionFunctionArgs,
} from "react-router";
import { api } from "@/lib/api";
import { formError, type FormError } from "@/lib/session";
import {
  AuthCard,
  ErrorNote,
  Field,
  RESEND_AFTER_SECONDS,
  ResendForm,
  SubmitButton,
} from "@/components/form";

/**
 * Asking for a link to choose a new password — or a first one, for an
 * account that signs in with Google. The api answers the same
 * whatever the address: one with no account is emailed a sign-up link
 * instead, so the page says only that an email went out.
 */

type ForgotResult = Partial<FormError> & { email?: string; askedAt?: number };

export async function forgotPasswordAction({ request }: ActionFunctionArgs): Promise<ForgotResult> {
  const form = await request.formData();
  const email = String(form.get("email") ?? "").trim();
  try {
    await api.auth.forgotPassword(email);
  } catch (error) {
    return { ...formError(error, "Could not send the link"), email };
  }
  return { email, askedAt: Date.now() };
}

export default function ForgotPassword() {
  const result = useActionData() as ForgotResult | undefined;
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const submitting = navigation.state === "submitting";

  if (result?.askedAt && result.email) {
    return <LinkSent key={result.askedAt} email={result.email} />;
  }

  return (
    <AuthCard
      title="Forgot your password?"
      footer={
        <Link to="/sign-in" className="font-medium underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <p className="text-sm text-muted-foreground">
        We'll email you a link to choose a new one. It also sets a first password for an
        account that signs in with Google.
      </p>
      {result?.error && !result.field && <ErrorNote message={result.error} />}
      <Form method="post" replace className="space-y-4">
        <Field
          label="Email"
          name="email"
          type="email"
          // The account's identifier, so a password manager fills it.
          autoComplete="username"
          autoFocus
          defaultValue={result?.email ?? searchParams.get("email") ?? undefined}
          error={result?.field === "email" ? result.error : undefined}
        />
        <SubmitButton pending={submitting}>{submitting ? "Sending…" : "Email me a link"}</SubmitButton>
      </Form>
    </AuthCard>
  );
}

function LinkSent({ email }: { email: string }) {
  return (
    <AuthCard
      title="Check your email"
      footer={
        <Link to="/sign-in" className="font-medium underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <p className="text-sm text-muted-foreground">
        We sent an email to <strong>{email}</strong> with a link to choose a new password. It
        works once and expires in an hour. If no account uses that address, the email says so
        and offers to create one.
      </p>
      <ResendForm fields={{ email }} wait={RESEND_AFTER_SECONDS} />
      <p className="text-xs text-muted-foreground">Not there? Check your spam or junk folder.</p>
    </AuthCard>
  );
}
